import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Card, Flex, Input, Progress, Tag, Typography } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import DataTable from "ui/DataTable"
import { numberToCurrency } from "helpers/string"
import dayjs from "dayjs"
import styles from "./TipsDetails.module.css"

const { Text } = Typography

const getValueByPath = (object, path) => {
  if (!object || !path) return undefined
  return path.split(".").reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), object)
}

const getRecordValue = (record, keys = [], fallback = "") => {
  for (const key of keys) {
    const value = key.includes(".") ? getValueByPath(record, key) : record?.[key]
    if (value !== undefined && value !== null && value !== "") return value
  }
  return fallback
}

const toCrewMembers = (record) => {
  if (Array.isArray(record?.crew_members)) {
    return record.crew_members
      .map((member) => member?.full_name || member?.name || `${member?.first_name || ""} ${member?.last_name || ""}`.trim())
      .filter(Boolean)
  }
  if (Array.isArray(record?.crew_member_names)) return record.crew_member_names.filter(Boolean)
  if (Array.isArray(record?.staff_names)) return record.staff_names.filter(Boolean)
  if (Array.isArray(record?.crew)) return record.crew.map((m) => m?.full_name || m?.name).filter(Boolean)
  if (Array.isArray(record?.staff)) return record.staff.map((m) => m?.full_name || m?.name).filter(Boolean)
  const single = getRecordValue(record, ["crew_member_name", "staff_name", "crew_member.full_name", "crew_member.name"], "")
  return single ? [single] : []
}

const toTipAmount = (record) => {
  const amount = Number(getRecordValue(record, ["tip_amount", "tips_amount", "amount", "tip.amount", "tip.value", "tip_income"], 0))
  return Number.isNaN(amount) ? 0 : amount
}

const toTableRow = (record) => {
  const crewMembers = toCrewMembers(record)
  const experienceDateRaw = getRecordValue(record, ["experience_date", "trip_date", "booked_date", "date"], null)
  const paidDateRaw = getRecordValue(record, ["paid_date", "paid_at", "tip_paid_date", "tip.paid_at", "created_at", "date"], null)
  const bookingRef = getRecordValue(
    record,
    ["booking_reference", "booking_ref", "booking_uuid", "booking_id", "booking.reference", "booking.code", "booking.uuid"],
    "-"
  )
  const paidDate = paidDateRaw ? dayjs(paidDateRaw).format("DD MMM YYYY") : "-"

  return {
    key: record?.id || `${bookingRef}-${paidDate}`,
    id: record?.id,
    bookingRef,
    experience: getRecordValue(record, ["experience_name", "trip_name", "experience.name", "experience.title", "trip.name"], "-"),
    experienceDate: experienceDateRaw ? dayjs(experienceDateRaw).format("DD MMM YYYY") : "-",
    client: getRecordValue(record, ["client_name", "customer_name", "guest_name", "client.full_name", "client.name", "customer.name"], "-"),
    crewMembers,
    paidDate,
    tipAmount: toTipAmount(record)
  }
}

TipsDetails.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    crew_members: PropTypes.array,
    tip_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tips_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tip: PropTypes.object,
    experience_date: PropTypes.string,
    trip_date: PropTypes.string,
    booked_date: PropTypes.string,
    date: PropTypes.string,
    paid_date: PropTypes.string,
    paid_at: PropTypes.string,
    tip_paid_date: PropTypes.string,
    created_at: PropTypes.string,
    booking_reference: PropTypes.string,
    booking_ref: PropTypes.string,
    booking_uuid: PropTypes.string,
    booking_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    booking: PropTypes.object,
    experience_name: PropTypes.string,
    trip_name: PropTypes.string,
    experience: PropTypes.object,
    trip: PropTypes.object,
    client_name: PropTypes.string,
    customer_name: PropTypes.string,
    guest_name: PropTypes.string,
    client: PropTypes.object,
    customer: PropTypes.object,
    crew_member_names: PropTypes.array,
    staff_names: PropTypes.array,
    crew: PropTypes.array,
    staff: PropTypes.array,
    crew_member_name: PropTypes.string,
    staff_name: PropTypes.string,
    crew_member: PropTypes.object
  })),
  crewMembers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    staff_split_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }))
}

export default function TipsDetails({ items = [], crewMembers = [] }) {
  const [search, setSearch] = useState("")

  const rows = useMemo(() => items.map(toTableRow), [items])

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return rows
    return rows.filter(
      (row) =>
        row.bookingRef.toLowerCase().includes(term) ||
        row.experience.toLowerCase().includes(term) ||
        row.client.toLowerCase().includes(term) ||
        row.crewMembers.some((name) => name.toLowerCase().includes(term))
    )
  }, [rows, search])

  const totalTips = useMemo(() => filteredRows.reduce((sum, row) => sum + row.tipAmount, 0), [filteredRows])

  const crewTipCounts = useMemo(() => {
    const counts = {}
    items.forEach((tip) => {
      if (Array.isArray(tip.crew_members)) {
        tip.crew_members.forEach(({ id }) => {
          counts[id] = (counts[id] || 0) + 1
        })
      }
    })
    return counts
  }, [items])

  const crewSideData = useMemo(
    () =>
      crewMembers
        .map((m) => ({ name: m.full_name, amount: Number(m.staff_split_amount) || 0, count: crewTipCounts[m.id] || 0 }))
        .sort((a, b) => b.amount - a.amount),
    [crewMembers, crewTipCounts]
  )

  const maxCrewAmount = crewSideData[0]?.amount || 0

  const columns = [
    {
      title: "Booking reference",
      dataIndex: "bookingRef",
      key: "bookingRef",
      sorter: (a, b) => a.bookingRef.localeCompare(b.bookingRef),
      render: (value) => <Text strong>{value}</Text>
    },
    {
      title: "Experience",
      key: "experience",
      sorter: (a, b) => a.experience.localeCompare(b.experience),
      render: (_, row) => (
        <Flex vertical className={styles.experienceCell}>
          <Text className={styles.truncateText}>{row.experience}</Text>
          <Text type="secondary" className={styles.cellSubtext}>
            {row.experienceDate}
          </Text>
        </Flex>
      )
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      sorter: (a, b) => a.client.localeCompare(b.client),
      render: (value) => <span className={styles.truncateText}>{value}</span>
    },
    {
      title: "Crew members",
      key: "crewMembers",
      render: (_, row) =>
        row.crewMembers.length ? (
          <Flex gap={6} wrap>
            {row.crewMembers.map((crewName) => (
              <Tag key={`${row.key}-${crewName}`} className={styles.crewTag}>
                {crewName}
              </Tag>
            ))}
          </Flex>
        ) : (
          <Text type="secondary">-</Text>
        )
    },
    {
      title: "Tip paid date",
      dataIndex: "paidDate",
      key: "paidDate",
      sorter: (a, b) => new Date(a.paidDate) - new Date(b.paidDate)
    },
    {
      title: "Tip amount",
      key: "tipAmount",
      align: "right",
      sorter: (a, b) => a.tipAmount - b.tipAmount,
      render: (_, row) => <Text strong>{numberToCurrency(row.tipAmount)}</Text>
    }
  ]

  const summaryRow = filteredRows.length ? (
    <tr className={styles.summaryRow}>
      <td colSpan={5} className="ant-table-cell">
        <Text strong>{`Total (${filteredRows.length} ${filteredRows.length === 1 ? "tip" : "tips"})`}</Text>
      </td>
      <td className="ant-table-cell" style={{ textAlign: "right" }}>
        <Text strong>{numberToCurrency(totalTips)}</Text>
      </td>
    </tr>
  ) : null

  return (
    <div className={styles.layout}>
      <Flex vertical gap={12}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by booking ref, experience, client or crew member"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <DataTable
          rowClickKey="key"
          dataSource={filteredRows}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} tips` }}
          scroll={{ x: 980 }}
          className={styles.table}
          locale={{ emptyText: "No tips found for selected filters." }}
          summary={() => summaryRow}
        />
      </Flex>

      <Card className={styles.sideCard}>
        <Flex vertical gap={12}>
          <div>
            <Text strong className={styles.sideTitle}>
              Tips by Crew Member
            </Text>
            <div>
              <Text type="secondary">Based on current filters</Text>
            </div>
          </div>

          {crewSideData.length ? (
            crewSideData.map((entry) => (
              <div key={entry.name} className={styles.crewRow}>
                <Flex justify="space-between" align="center">
                  <Text className={styles.crewName}>{entry.name}</Text>
                  <Text strong>{numberToCurrency(entry.amount)}</Text>
                </Flex>
                <Progress
                  percent={maxCrewAmount ? Math.round((entry.amount / maxCrewAmount) * 100) : 0}
                  showInfo={false}
                  size={{ height: 6 }}
                  strokeColor="var(--color-primary-second)"
                  trailColor="#e8edf5"
                />
                <Flex justify="flex-end">
                  <Text type="secondary" className={styles.tipCount}>
                    {entry.count} {entry.count === 1 ? "tip" : "tips"}
                  </Text>
                </Flex>
              </div>
            ))
          ) : (
            <Flex justify="center" align="center" className={styles.noData}>
              <Text type="secondary">No tips found for selected filters.</Text>
            </Flex>
          )}
        </Flex>
      </Card>
    </div>
  )
}
